import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import Slider from 'react-rangeslider';

import ProgressBar from '../../ui/ProgressBar';
import {
  setSelectedVariantAndUpdateRange,
  setSelectedVariant,
  setGenomeBrowserWarning,
  updateGenomicRange,
} from '../../../actions/GenomeActions';

import {
  fetchDeepSEAFeaturePredictions,
  fetchDeepSEAFeaturesAndPredictions,
  fetchDeepSEAJobFeaturePredictions,
  fetchDeepSEAJobFeaturesAndPredictions,
} from '../../../actions/DeepSEAActions';
import Button from '../../ui/Button';
import { isExPecto } from '../../../core/util';
import styles from './FeatureHeatmap.css';
import Heatmap from '../../Heatmap';
import FeatureHeatmapHeader from './FeatureHeatmapHeader';

const BUFFER_DISTANCE = 50000;
const MIN_ROW_LIMIT = 20;
const ROW_INCREMENT = 10;
const HEATMAP_CELL_WIDTH = 15;

/*
 * Pure functions for creating heatmap data
 */
function getCenterVariant({
  chromosome,
  startPosition,
  endPosition,
  featurePredictions,
  selectedVariant,
  dispatch,
  warning,
}) {
  // Get center variant based on position and the featurePredictions
  const centerPosition = Math.floor(
    parseInt((endPosition + startPosition) / 2, 10),
  );
  let centerVariantDistance = -1;
  let centerVariantIdx = -1;
  let centerVariants = [];
  for (let i = 0; i < featurePredictions.length; i += 1) {
    const variant = featurePredictions[i];
    const variantPosition = variant.position;
    const variantChr = variant.chromosome;
    if (
      chromosome === variantChr &&
      startPosition <= variantPosition &&
      endPosition >= variantPosition
    ) {
      // Check each featurePredictions and grab all of them within the correct chr and range
      const variantDistance = Math.abs(centerPosition - variantPosition);
      if (
        centerVariantDistance < 0 ||
        variantDistance < centerVariantDistance
      ) {
        centerVariantDistance = variantDistance;
        centerVariantIdx = i;
      }
    }
  }
  if (centerVariantIdx !== -1) {
    const centerVariant = featurePredictions[centerVariantIdx];
    // Get all center variants with the same chromosome and position
    centerVariants = featurePredictions.filter(
      variant =>
        variant.chromosome === centerVariant.chromosome &&
        variant.position === centerVariant.position,
    );
    // Check if selectedVariant exists and is the same as the center variant
    // and select the one with the same allele pairing
    if (
      selectedVariant &&
      centerVariant.chromosome === selectedVariant.chromosome &&
      centerVariant.position === selectedVariant.position
    ) {
      const selectedVariantIdx = featurePredictions.findIndex(
        variant =>
          variant.chromosome === selectedVariant.chromosome &&
          variant.ref_allele === selectedVariant.ref_allele &&
          variant.alt_allele === selectedVariant.alt_allele &&
          variant.position === selectedVariant.position &&
          variant.position >= startPosition &&
          variant.position <= endPosition,
      );

      centerVariantIdx =
        selectedVariantIdx !== -1 ? selectedVariantIdx : centerVariantIdx;
    }
  }

  // Check if there are multiples of the same center Variant, and if the variant
  // value is the same as the stored warning value
  // eslint-disable-next-line eqeqeq
  if (centerVariants[1] != warning) {
    // Need to compare between null and undefined
    if (centerVariants.length > 1) {
      dispatch(setGenomeBrowserWarning(centerVariants[1]));
    } else dispatch(setGenomeBrowserWarning(null));
  }
  return centerVariantIdx;
}

function getHeatmapRows({
  featurePredictions,
  filteredFeatures,
  centerVariantIdx,
  reverseOrder,
  rowLimit,
  selectedFeatureScoreType,
}) {
  // Sort features by center variant value
  let sortedFeatures = filteredFeatures.map(feature => ({
    feature,
    v: parseFloat(
      featurePredictions[centerVariantIdx].feature_predictions[feature.idx],
    ),
  }));

  sortedFeatures.sort((a, b) => (Math.abs(a.v) > Math.abs(b.v) ? -1 : 1));
  if (
    (reverseOrder && !selectedFeatureScoreType.reverse_order) ||
    (!reverseOrder && selectedFeatureScoreType.reverse_order)
  )
    sortedFeatures.reverse();

  sortedFeatures = sortedFeatures.slice(
    0,
    Math.min(sortedFeatures.length, rowLimit),
  );

  return isExPecto(selectedFeatureScoreType)
    ? sortedFeatures.map(f => ({
        name: f.feature.title,
        feature: f.feature,
        link: false,
      }))
    : sortedFeatures.map(f => ({
        name:
          f.feature.cell_type.name !== 'None'
            ? `${f.feature.cell_type.name.replace(/_/g, ' ')} /
        ${f.feature.chromatin_feature_type.name}`
            : f.feature.chromatin_feature_type.name,
        feature: f.feature,
        link: false,
      }));
}

function getHeatmapColumns({
  containerWidth,
  heatmapViewType,
  chromosome,
  startPosition,
  endPosition,
  selectedVariant,
  variantData,
  featurePredictions,
  selectedVariantScoreType,
  dispatch,
}) {
  // Create heatmap columns and find the selected variant based on position
  const totalColumns = containerWidth / HEATMAP_CELL_WIDTH;
  const genomeColumns = Array(Math.floor(totalColumns)).fill(null);
  const columnGenomeInterval = (endPosition - startPosition) / totalColumns;
  const centerColumn = Math.floor(
    (selectedVariant.position - startPosition) / columnGenomeInterval,
  );
  let selectedGenomicIdx = null;
  const selectedIndex = featurePredictions.findIndex(
    variant =>
      selectedVariant &&
      variant.chromosome === selectedVariant.chromosome &&
      variant.ref_allele === selectedVariant.ref_allele &&
      variant.alt_allele === selectedVariant.alt_allele &&
      variant.position === selectedVariant.position,
  );
  const findSelectedGenomicIdx = genomeArray =>
    genomeArray.findIndex(
      variant => variant && variant.variantIdx === selectedIndex,
    );
  const onClick = variant => {
    dispatch(
      setSelectedVariantAndUpdateRange(variant, {
        chromosome,
        startPosition,
        endPosition,
      }),
    );
  };

  featurePredictions.forEach((variant, idx) => {
    const variantPosition = variant.position;
    const variantChr = variant.chromosome;
    if (
      chromosome === variantChr &&
      startPosition <= variantPosition &&
      endPosition >= variantPosition
    ) {
      let columnGenomeIdx = Math.floor(
        (variantPosition - startPosition) / columnGenomeInterval,
      );
      // Get the current variant score based on the scoreType to compare against for collapsed view
      let variantScore = null;
      if (variantData) {
        const result = variantData.find(
          currentVariant =>
            currentVariant.chromosome === variantChr &&
            currentVariant.position === variantPosition &&
            currentVariant.ref_allele === variant.ref_allele &&
            currentVariant.alt_allele === variant.alt_allele,
        );
        variantScore = result[selectedVariantScoreType.short_name];
      }

      const onClickVariant = {
        chromosome: variantChr,
        position: variantPosition,
        ref_allele: variant.ref_allele,
        alt_allele: variant.alt_allele,
      };

      const column = {
        name: `${variantPosition} ${
          variant.ref_allele ? variant.ref_allele : ''
        }${variant.ref_allele ? '>' : ''}${
          variant.alt_allele ? variant.alt_allele : ''
        }`,
        onClick: () => onClick(onClickVariant),
        variantIdx: idx,
        variantScore,
      };
      selectedGenomicIdx = findSelectedGenomicIdx(genomeColumns);
      if (!genomeColumns[columnGenomeIdx]) {
        // No variants occupy this column
        genomeColumns[columnGenomeIdx] = column;
      } else if (heatmapViewType === 'full') {
        while (
          genomeColumns[columnGenomeIdx] &&
          columnGenomeIdx < genomeColumns.length
        ) {
          columnGenomeIdx += 1;
        }
        // Center the selected variant, if selected variant isn't centered push the current columns back until
        //   selectedVariant is centered
        selectedGenomicIdx = findSelectedGenomicIdx(genomeColumns);
        if (columnGenomeIdx < genomeColumns.length) {
          genomeColumns[columnGenomeIdx] = column;
        } else if (selectedGenomicIdx !== centerColumn) {
          genomeColumns.shift();
          genomeColumns.push(column);
        }
      } else if (
        selectedIndex === idx ||
        (genomeColumns[columnGenomeIdx].variantScore < variantScore &&
          columnGenomeIdx !== selectedGenomicIdx)
      ) {
        // Switch scores if current score is greater then the score at this column is greater then saved score,
        //   or if it's the selected variant
        genomeColumns[columnGenomeIdx] = column;
      }
    }
  });

  // This will center the selectedVariant in the heatmap if it is too far to the right
  selectedGenomicIdx = findSelectedGenomicIdx(genomeColumns);
  while (selectedGenomicIdx > centerColumn) {
    genomeColumns.shift();
    genomeColumns.push(null);
    selectedGenomicIdx = findSelectedGenomicIdx(genomeColumns);
  }

  return genomeColumns.map(
    (column, idx) => column || { name: idx, hidden: true, variantIdx: -1 },
  );
}

function getHeatmapValues({ heatmapRows, heatmapColumns, featurePredictions }) {
  // Generate heatmap values based on rows and columns
  const heatmapValues = [];
  for (let i = 0; i < heatmapRows.length; i += 1) {
    const featureIdx = heatmapRows[i].feature.idx;
    for (let j = 0; j < heatmapColumns.length; j += 1) {
      const { variantIdx } = heatmapColumns[j];
      if (variantIdx > -1) {
        const value = {
          value: featurePredictions[variantIdx].feature_predictions[featureIdx],
          row: i,
          column: j,
        };
        heatmapValues.push(value);
      }
    }
  }
  return heatmapValues;
}

export const FeatureHeatmap = ({
  containerWidth,
  color,
  defaultFeatureScoreCutoffs,
  setDefaultFeatureScoreCutoffs,
  featureScoreOptions,
  variantData,
  heatmapLabels,
}) => {
  const [rowLimit, setRowLimit] = useState(MIN_ROW_LIMIT);
  const [searchText, setSearchText] = useState('');
  const [reverseOrder, setReverseOrder] = useState(false);
  const [heatmapViewType, setHeatmapViewType] = useState('full');
  const [heatmap, setHeatmap] = useState('');
  const [heatmapData, setHeatmapData] = useState({
    rows: [],
    columns: [],
    values: [],
    centerColumn: -1,
  });
  const [featureScoreCutoffs, setFeatureScoreCutoffs] = useState(
    defaultFeatureScoreCutoffs,
  );
  const dispatch = useDispatch();
  const {
    features,
    featurePredictions,
    isFetchingFeaturePredictions,
    selectedVariantScoreType,
    selectedFeatureScoreType,
    selectedJob,
    expectoFeatures,
  } = useSelector(state => state.deepsea);

  const {
    selectedVariant,
    chromosome,
    startPosition,
    endPosition,
    warning,
  } = useSelector(state => state.genome);
  const buffer = useRef({
    startPosition,
    endPosition,
    chromosome,
    featurePredictions,
  });
  const startPositionRef = useRef(startPosition);
  const endPositionRef = useRef(endPosition);

  const filteredFeatures = useMemo(() => {
    if (features === undefined && expectoFeatures === undefined) return [];
    const search = searchText.toLowerCase();
    return isExPecto(selectedFeatureScoreType) && expectoFeatures
      ? expectoFeatures.filter(
          ftr => ftr.title.toLowerCase().search(search) !== -1,
        )
      : features?.filter(
          ftr =>
            ftr.cell_type.name.toLowerCase().search(search) !== -1 ||
            ftr.chromatin_feature_type.name.toLowerCase().search(search) !== -1,
        );
  }, [features, searchText, selectedFeatureScoreType, expectoFeatures]);

  const windowOutsideBuffer =
    endPosition > buffer.current.endPosition ||
    startPosition < buffer.current.startPosition ||
    chromosome !== buffer.current.chromosome;

  const showShrinkButton =
    rowLimit > MIN_ROW_LIMIT && filteredFeatures.length > MIN_ROW_LIMIT;
  const showExpandButton =
    heatmapData.rows.length > 0 &&
    filteredFeatures.length > heatmapData.rows.length;
  const sortDirectionText = reverseOrder ? 'bottom' : 'top';
  const headerText = (
    <p style={{ margin: '0 0 5px 10px' }}>
      {`Showing ${showExpandButton ? sortDirectionText : 'all'} ${
        heatmapData.rows.length
      }${showExpandButton ? ` of ${filteredFeatures.length}` : ''}`}
    </p>
  );

  function featureTooltip(row, col, value) {
    const treatment = row.feature.treatment.name;
    const celltype = row.feature.cell_type.name.replace(/_/g, ' ');
    const featuretype = row.feature.chromatin_feature_type.name;

    return (
      <div style={{ textAlign: 'left' }} className={styles.tooltip}>
        <table>
          <tbody>
            <tr>
              <td>Cell-type:</td>
              <td>{celltype}</td>
            </tr>
            <tr>
              <td>Feature-type:</td>
              <td>{featuretype}</td>
            </tr>
            <tr>
              <td>Treatment:</td>
              <td>{treatment && treatment !== 'None' && treatment}</td>
            </tr>
            <tr>
              <td>Position:</td>
              <td>{`${chromosome}:${col.name}`}</td>
            </tr>
            <tr>
              <td>Score:</td>
              <td>{Number.parseFloat(value).toPrecision(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  function expectoTooltip(row, col, value) {
    return (
      <div style={{ textAlign: 'left' }} className={styles.tooltip}>
        <table>
          <tbody>
            <tr>
              <td>Feature-type:</td>
              <td>{row.name}</td>
            </tr>
            <tr>
              <td>Position:</td>
              <td>{`${chromosome}:${col.name}`}</td>
            </tr>
            <tr>
              <td>Score:</td>
              <td>{Number.parseFloat(value).toPrecision(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  function heatmapTooltip(cell) {
    const row = heatmapData.rows[cell.row];
    const col = heatmapData.columns[cell.column];
    const val = cell.value;

    // Defensive check: cell indices may be stale after data updates (e.g., during drag)
    if (!row || !col) return null;

    if (
      !buffer.current.featurePredictions[col.variantIdx] ||
      !selectedFeatureScoreType
    ) {
      return null;
    }
    return isExPecto(selectedFeatureScoreType)
      ? expectoTooltip(row, col, val)
      : featureTooltip(row, col, val);
  }

  function heatmapStyles({ rowHeaderWidth }) {
    const leftShift = !Number.isNaN(rowHeaderWidth) ? rowHeaderWidth : 10;
    return {
      marginLeft: -leftShift,
      width: leftShift + containerWidth + 50,
    };
  }

  function handleHeatmapDrag(e) {
    if (Math.abs(e.dx) > 100 || e.dx === 0) return;
    const bpPerPix =
      (endPositionRef.current - startPositionRef.current) / containerWidth;
    const bpChange = Math.floor(-1 * e.dx * bpPerPix);

    startPositionRef.current += bpChange;
    endPositionRef.current += bpChange;

    dispatch(
      updateGenomicRange(
        chromosome,
        startPositionRef.current + bpChange,
        endPositionRef.current + bpChange,
      ),
    );
  }

  useEffect(() => {
    // If the feature score type or cutoff changes, fetch new features and predictions
    setDefaultFeatureScoreCutoffs(featureScoreCutoffs);
    const chrNum = chromosome.replace('chr', '');
    if (selectedJob?.job_id && selectedFeatureScoreType?.name) {
      const params = {
        score_type: selectedFeatureScoreType.name,
        chr_and_variant_range: `${chrNum}:${startPosition}:${endPosition}`,
      };
      if (isExPecto(selectedFeatureScoreType)) params.expecto = true;

      dispatch(
        fetchDeepSEAJobFeaturesAndPredictions(
          selectedJob.job_id,
          selectedJob.ds_model,
          params,
        ),
      );
    } else if (featureScoreCutoffs[selectedFeatureScoreType?.track?.name]) {
      const params = {
        chr_and_variant_range: `${chrNum}:${startPosition}:${endPosition}`,
        variant_cutoff:
          featureScoreCutoffs[selectedFeatureScoreType.track.name],
      };
      if (isExPecto(selectedFeatureScoreType)) params.expecto = true;

      dispatch(
        fetchDeepSEAFeaturesAndPredictions(
          selectedFeatureScoreType.name,
          params,
        ),
      );
    } else {
      const params = {
        score_type: selectedFeatureScoreType.name,
        chr_and_variant_range: `${chrNum}:${startPosition}:${endPosition}`,
      };
      if (isExPecto(selectedFeatureScoreType)) params.expecto = true;

      dispatch(
        fetchDeepSEAFeaturesAndPredictions(
          selectedFeatureScoreType.name,
          params,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeatureScoreType, featureScoreCutoffs]);

  useEffect(() => {
    if (windowOutsideBuffer) {
      // If part of the current genome window intersects with the buffer, add the new feature predictions to the buffer
      if (
        buffer.current.chromosome === chromosome &&
        ((startPosition <= buffer.current.endPosition &&
          startPosition >= buffer.current.startPosition) ||
          (endPosition <= buffer.current.endPosition &&
            endPosition >= buffer.current.startPosition))
      ) {
        let newEndPosition = null;
        let newStartPosition = null;
        if (endPosition >= buffer.current.endPosition) {
          // New position is greater than previous position
          newEndPosition = endPosition + BUFFER_DISTANCE;
          newStartPosition = buffer.current.endPosition;
          if (newEndPosition < 0) newEndPosition = 0;
        } else {
          // New position is less than previous position
          newEndPosition = buffer.current.startPosition;
          newStartPosition = startPosition - BUFFER_DISTANCE;
          if (newStartPosition < 0) newStartPosition = 0;
        }

        buffer.current = {
          ...buffer.current,
          startPosition: Math.min(
            newStartPosition,
            buffer.current.startPosition,
          ),
          endPosition: Math.max(newEndPosition, buffer.current.endPosition),
        };
      } else {
        // Should only run the boundaries of the buffer do not intersect with the current positions
        buffer.current = {
          chromosome,
          startPosition,
          endPosition,
          featurePredictions: [],
        };
      }
      const {
        chromosome: newChr,
        startPosition: newStartPosition,
        endPosition: newEndPosition,
      } = buffer.current;
      const chrNum = newChr.replace('chr', '');
      if (selectedJob.job_id) {
        dispatch(
          fetchDeepSEAJobFeaturePredictions(selectedJob.job_id, {
            score_type: selectedFeatureScoreType.name,
            chr_and_variant_range: `${chrNum}:${newStartPosition}:${newEndPosition}`,
          }),
        );
      } else if (featureScoreCutoffs[selectedFeatureScoreType.track.name]) {
        dispatch(
          fetchDeepSEAFeaturePredictions(selectedFeatureScoreType.name, {
            chr_and_variant_range: `${chrNum}:${newStartPosition}:${newEndPosition}`,
            variant_cutoff:
              featureScoreCutoffs[selectedFeatureScoreType.track.name],
          }),
        );
      } else {
        dispatch(
          fetchDeepSEAFeaturePredictions(selectedFeatureScoreType.name, {
            chr_and_variant_range: `${chrNum}:${newStartPosition}:${newEndPosition}`,
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chromosome, startPosition, endPosition]);

  /*
   * Called whenever the heatmap should change
   */
  useEffect(() => {
    if (!windowOutsideBuffer) {
      // Set position ref's for onClick
      startPositionRef.current = startPosition;
      endPositionRef.current = endPosition;
      // Remove all the buffered feature Predictions within the range of the current feature predictions
      let newFeaturePredictions = buffer.current.featurePredictions.filter(
        featurePrediction =>
          featurePrediction.position <= startPosition ||
          featurePrediction.position >= endPosition,
      );
      // Add current feature predictinos to the buffer and get the unique values
      newFeaturePredictions = newFeaturePredictions.concat(featurePredictions);
      const uniqueVariants = {};
      newFeaturePredictions.forEach(variant => {
        if (
          buffer.current.chromosome === variant.chromosome &&
          buffer.current.startPosition <= variant.position &&
          buffer.current.endPosition >= variant.position
        )
          uniqueVariants[
            `${variant.position} ${variant.ref_allele}/${variant.alt_allele}`
          ] = variant;
      });
      newFeaturePredictions = Object.values(uniqueVariants);
      buffer.current.featurePredictions = newFeaturePredictions;
      // Update heatmap based on buffer
      const centerVariantIdx = getCenterVariant({
        chromosome,
        startPosition,
        endPosition,
        featurePredictions: buffer.current.featurePredictions,
        selectedVariant,
        dispatch,
        warning,
      });
      const centerVariant = buffer.current.featurePredictions[centerVariantIdx];
      if (centerVariant) {
        // Update heatmap only if there is a center variant, else there are no feature predictions
        const rows = getHeatmapRows({
          featurePredictions: buffer.current.featurePredictions,
          filteredFeatures,
          centerVariantIdx,
          reverseOrder,
          rowLimit,
          selectedFeatureScoreType,
        });
        const columns = getHeatmapColumns({
          featurePredictions: buffer.current.featurePredictions,
          dispatch,
          selectedVariant: centerVariant,
          containerWidth,
          chromosome,
          startPosition,
          endPosition,
          selectedVariantScoreType,
          variantData,
          heatmapViewType,
        });
        const values = getHeatmapValues({
          heatmapRows: rows,
          heatmapColumns: columns,
          featurePredictions: buffer.current.featurePredictions,
        });
        const centerColumn = columns.findIndex(
          column => column && column.variantIdx === centerVariantIdx,
        );
        setHeatmapData({
          rows,
          columns,
          values,
          centerColumn,
        });
        if (
          centerVariant.chromosome !== selectedVariant.chromosome ||
          centerVariant.position !== selectedVariant.position ||
          centerVariant.ref_allele !== selectedVariant.ref_allele ||
          centerVariant.alt_allele !== selectedVariant.alt_allele
        )
          // Only update the selectedVariant if it isn't already the centerVariant
          dispatch(
            setSelectedVariant({
              chromosome: centerVariant.chromosome,
              position: centerVariant.position,
              ref_allele: centerVariant.ref_allele,
              alt_allele: centerVariant.alt_allele,
            }),
          );
      } else {
        setHeatmapData({
          rows: [],
          columns: [],
          values: [],
          centerColumn: -1,
        });
        if (Object.keys(selectedVariant).length !== 0)
          // Deselect variant if it goes out of view on heatmap
          dispatch(setSelectedVariant({}));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chromosome,
    startPosition,
    endPosition,
    features,
    featurePredictions,
    selectedVariant,
    containerWidth,
    heatmapViewType,
    searchText,
    selectedVariantScoreType,
    rowLimit,
    reverseOrder,
  ]);

  useEffect(() => {
    setHeatmap(
      <Heatmap
        columnValues={heatmapData.columns}
        rowValues={heatmapData.rows}
        heatmapValues={heatmapData.values}
        tooltips={heatmapTooltip}
        scrollIntoView={false}
        heatmapInfo={{ targetXIndex: heatmapData.centerColumn }}
        color={color}
        style={heatmapStyles}
        maxBodyWidth={containerWidth + 50}
        onDrag={handleHeatmapDrag}
      />,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, containerWidth, heatmapData]);

  return (
    <div>
      <FeatureHeatmapHeader
        color={color}
        headerText={headerText}
        heatmapLabels={heatmapLabels}
        reverseOrder={reverseOrder}
        featureScoreOptions={featureScoreOptions}
        heatmapViewType={heatmapViewType}
        setReverseOrder={setReverseOrder}
        setSearchText={setSearchText}
        setHeatmapViewType={setHeatmapViewType}
      />
      <div
        style={{
          clear: 'both',
          position: 'relative',
          top: -25,
        }}
      >
        {selectedFeatureScoreType &&
          selectedFeatureScoreType.track &&
          featureScoreCutoffs[selectedFeatureScoreType.track.name] && (
            <div style={{ paddingTop: 20, width: 520 }}>
              <span>
                {`Minimum variant score cutoff:
                ${featureScoreCutoffs[
                  selectedFeatureScoreType.track.name
                ].toFixed(1)}`}
              </span>
              <Slider
                min={1}
                max={3}
                step={0.1}
                value={featureScoreCutoffs[selectedFeatureScoreType.track.name]}
                onChange={score => {
                  setFeatureScoreCutoffs(
                    Object.assign({}, featureScoreCutoffs, {
                      [selectedFeatureScoreType.track.name]: score,
                    }),
                  );
                }}
              />
            </div>
          )}
        <div className={styles.heatmap}>
          {isFetchingFeaturePredictions ? (
            <ProgressBar
              label="Retrieving predictions..."
              style={{ marginTop: '100px' }}
            />
          ) : (
            heatmap
          )}
        </div>
        <div style={{ textAlign: 'center', clear: 'both' }}>
          {showExpandButton && !isFetchingFeaturePredictions && (
            <Button
              bsStyle="link"
              onClick={() => {
                setRowLimit(rowLimit + ROW_INCREMENT);
              }}
              style={{ color: 'gray', padding: 0 }}
            >
              <i className="fa fa-angle-down fa-4x" />
            </Button>
          )}
          {showShrinkButton && !isFetchingFeaturePredictions && (
            <Button
              bsStyle="link"
              onClick={() => {
                setRowLimit(Math.max(MIN_ROW_LIMIT, rowLimit - ROW_INCREMENT));
              }}
              style={{ color: 'gray', padding: 0 }}
            >
              <i className="fa fa-angle-up fa-4x" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

FeatureHeatmap.propTypes = {
  containerWidth: PropTypes.number,
  color: PropTypes.func.isRequired,
  defaultFeatureScoreCutoffs: PropTypes.objectOf(PropTypes.number),
  setDefaultFeatureScoreCutoffs: PropTypes.func,
  featureScoreOptions: PropTypes.arrayOf(PropTypes.object),
  variantData: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  heatmapLabels: PropTypes.object.isRequired,
};

FeatureHeatmap.defaultProps = {
  defaultFeatureScoreCutoffs: {},
  setDefaultFeatureScoreCutoffs: () => {},
  containerWidth: 1170,
  featureScoreOptions: [],
  variantData: null,
};

export default withStyles(styles)(FeatureHeatmap);
